// oxlint-disable no-unused-expressions
import mongoose, { Connection } from "mongoose";
import dotenv from "dotenv";

dotenv.config();

const BILLING_URI = `${process.env.MONGODB_URI}/billing`;
let billingConn: Connection | null = null;

export function connectToDatabase(): Connection {
  if (process.env.CI) {
    return {} as Connection;
  }

  if (billingConn && billingConn.readyState === 1) return billingConn;

  if (!billingConn) {
    billingConn = mongoose.createConnection(BILLING_URI);
  }

  return billingConn;
}

const ReplySchema = new mongoose.Schema(
  {
    content: { type: String, required: true },
    author: { type: String, required: true },
    isStaff: { type: Boolean, default: false },
    createdAt: { type: Date, default: Date.now },
    editedAt: { type: Date, default: null },
    readAt: { type: Date, default: null },
    private: { type: Boolean, default: false },
    source: { type: String, enum: ["console", "email", "billing", "admin"] },
  },
  { timestamps: false },
);

export const TICKET_ENUMS = {
  inquiryType: [
    "question_support",
    "staff_order_review",
    "sales_orders",
    "incidents_problems",
    "compute_provisioning",
    "network_provisioning",
    "transit_request",
    "peering_request",
    "abuse_report",
    "noc",
    "ix",
    "route_leak",
    "feedback",
    "ip_reassignment",
    "routed_tunnel",
    "order_cancellation",
    "unauthorized_announcement",
    "invoice_renewal",
    "failed_payment",
    "tos_warning",
    "tos_violation",
    "spam",
    "service_suspended",
    "service_terminated",
    "order_change",
    "legal_request",
  ],
  severity: ["low", "medium", "high", "urgent"],
  status: [
    "open",
    "pending",
    "on_hold",
    "waiting_for_provisioning",
    "waiting_for_reply",
    "waitlisted",
    "resolved",
    "closed",
  ],
  bgpAfi: ["v4", "v6", "dual_stack"],
  bgpMethod: ["vm_sessions", "ix_sessions", "tunnel", "cross_connect", "vlan"],
  orderStatus: [
    "active",
    "inactive",
    "suspended",
    "terminated",
    "pending",
    "incomplete",
    "overdue",
  ],
};

export const TicketSchema = new mongoose.Schema({
  inquiryType: {
    type: String,
    required: true,
  },
  name: { type: String, required: true },
  email: { type: String, required: true },
  to: { type: [String], default: [] },
  cc: { type: [String], default: [] },
  phone: { type: String },
  organization: { type: String },
  orderId: { type: String },
  severity: {
    type: String,
    required: true,
  },
  description: { type: String, required: true },
  userTarget: { type: String },
  targetTimeframe: { type: String },
  asn: { type: String },
  nocEmail: { type: String },
  asSet: { type: String },
  prefixes: { type: String },
  ipAddress: { type: String },
  timeframeOfAbuse: {
    type: String,
  },
  status: {
    type: String,
    required: true,
    default: "open",
  },
  source: {
    type: String,
    enum: ["console", "email", "billing", "admin"],
  },
  ticketId: { type: String, required: true, unique: true },
  replies: { type: [ReplySchema], default: [] },
  createdAt: { type: Date, default: Date.now },
  updatedAt: { type: Date, default: Date.now },
  purchaseDate: { type: Date },
  vmId: { type: String },
  vmOs: {
    type: String,
  },
  vmIpv4: { type: String },
  vmIpv6: { type: String },
  vmUsername: { type: String },
  vmPassword: { type: String },
  vmHostname: { type: String },
  vmV6Enabled: { type: String },
  vmBgpSessions: { type: String },
  vmlocation: { type: String },
  vmCustomerPanelAccess: { type: Boolean },
  bgpAsn: { type: String },
  bgpAsSet: { type: String },
  bgpAfi: { type: String },
  bgpMethod: { type: String },
  bgpDownstreams: { type: String },
  bgpNotes: { type: String },
  bgpAutNum: { type: String },
  abuseEmail: { type: String },
  bgpOurV4: { type: String },
  bgpOurV6: { type: String },
  ixIpv4: { type: String },
  ixIpv6: { type: String },
  ixSpeed: { type: String },
  ixName: { type: String },
  tunnelEndpoint: { type: String },
  tunnelType: { type: String },
  tunnelIpv4: { type: String },
  tunnelIpv6: { type: String },
  reassignmentSubnet: { type: String },
  ipType: { type: String },
  ipLocalIp: { type: String },
  ipTunnelType: { type: String },
  ipAsn: { type: String },
  ipNocEmail: { type: String },
  ipAbuseEmail: { type: String },
  ipArin: { type: String },
  ipRpki: { type: String },
  ipLoa: { type: String },
  ipNotes: { type: String },
  tags: { type: [String], default: [] },
  rating: {
    score: { type: Number, min: 1, max: 5, default: null },
    feedback: { type: String, default: null },
    submittedAt: { type: Date, default: null },
  },
  attributes: {},
});

const AttachmentSchema = new mongoose.Schema(
  {
    ticketId: { type: String, required: true, index: true },
    url: { type: String, required: true },
    filename: { type: String, required: true },
    uploadedBy: { type: String },
    uploadedAt: { type: Date, default: Date.now },
  },
  { timestamps: false },
);

const EmailLogSchema = new mongoose.Schema(
  {
    to: { type: [String], default: [] },
    from: { type: mongoose.Schema.Types.Mixed },
    subject: { type: String },
    html: { type: String },
    text: { type: String },
    cc: { type: [String], default: [] },
    bcc: { type: [String], default: [] },
    replyTo: { type: mongoose.Schema.Types.Mixed },
    attachments: { type: [mongoose.Schema.Types.Mixed], default: undefined },
    headers: { type: mongoose.Schema.Types.Mixed },
    status: { type: String, enum: ["sent", "failed"], required: true },
    provider: { type: String },
    messageId: { type: String },
    response: { type: String },
    envelope: { type: mongoose.Schema.Types.Mixed },
    errorMessage: { type: String },
    errorStack: { type: String },
    payload: { type: mongoose.Schema.Types.Mixed },
    sentAt: { type: Date },
  },
  { timestamps: { createdAt: "createdAt", updatedAt: false } },
);

const FeedbackSchema = new mongoose.Schema(
  {
    name: { type: String, required: true },
    email: { type: String, required: true },
    feedbackComments: { type: String, required: true },
    contactRegardingFeedback: { type: Boolean, required: true },
    submittedAt: { type: Date, default: Date.now },
  },
  { timestamps: false },
);

export function getTicketModel() {
  const conn = connectToDatabase();
  return conn.models.Ticket || conn.model("Ticket", TicketSchema);
}
export function getReplyModel() {
  const conn = connectToDatabase();
  return conn.models.Reply || conn.model("Reply", ReplySchema);
}
export function getAttachmentModel() {
  const conn = connectToDatabase();
  return conn.models.Attachment || conn.model("Attachment", AttachmentSchema);
}

export function getEmailLogModel() {
  const conn = connectToDatabase();
  return conn.models.EmailLog || conn.model("EmailLog", EmailLogSchema);
}

export function getFeedbackModel() {
  const conn = connectToDatabase();
  return conn.models.Feedback || conn.model("Feedback", FeedbackSchema);
}

const BlockedSenderSchema = new mongoose.Schema(
  {
    email: { type: String, required: true, unique: true, index: true },
    reason: { type: String },
    addedBy: { type: String },
    createdAt: { type: Date, default: Date.now },
  },
  { timestamps: false },
);

export function getBlockedSenderModel() {
  const conn = connectToDatabase();
  return (
    (conn.models as any).BlockedSender ||
    conn.model("BlockedSender", BlockedSenderSchema)
  );
}

export async function mergeTickets({
  primaryTicketId,
  secondaryTicketId,
  fieldsToMerge,
  adminName,
}: {
  primaryTicketId: string;
  secondaryTicketId: string;
  fieldsToMerge: string[];
  adminName: string;
}) {
  const Ticket = getTicketModel();
  (await Ticket.db.readyState) === 1
    ? Promise.resolve()
    : Ticket.db.openUri(BILLING_URI);
  const primary = await Ticket.findOne({ ticketId: primaryTicketId });
  const secondary = await Ticket.findOne({ ticketId: secondaryTicketId });

  if (!primary || !secondary) throw new Error("Ticket(s) not found");

  const shouldMergeDescription = fieldsToMerge.includes("description");

  // If requested, carry over the secondary ticket description as a reply
  // instead of overwriting the primary description field.
  if (shouldMergeDescription && secondary.description) {
    primary.replies.push({
      content: `[Merged from ticket ${secondaryTicketId}]\n\n${secondary.description}`,
      author: secondary.name || secondary.email || "Merged Ticket",
      isStaff: true,
      createdAt: secondary.createdAt || new Date(),
      private: false,
      source: "console",
    });
  }

  // Preserve existing replies and append replies from the secondary ticket.
  primary.replies = [
    ...primary.replies,
    ...secondary.replies.map((r: any) => {
      const reply = r.toObject();
      delete reply._id;
      return {
        ...reply,
        content: `[Merged from ticket ${secondaryTicketId}]\n\n${reply.content}`,
      };
    }),
  ];

  fieldsToMerge.forEach((field) => {
    if (["description", "replies"].includes(field)) return;
    if (secondary[field] !== undefined) {
      primary[field] = secondary[field];
    }
  });

  primary.replies.push({
    content: `Ticket [${secondaryTicketId}](https://tritan.gg/console/tickets/${secondaryTicketId}) was merged into this ticket by ${adminName}.`,
    author: "Helpdesk Bot 🤖",
    isStaff: true,
    createdAt: new Date(),
    private: false,
    source: "console",
  });

  secondary.replies.push({
    content: `This ticket was merged into [${primaryTicketId}](https://tritan.gg/console/tickets/${primaryTicketId}) by ${adminName}.`,
    author: "Helpdesk Bot 🤖",
    isStaff: true,
    createdAt: new Date(),
    private: false,
    source: "console",
  });

  secondary.status = "closed";

  await primary.save();
  await secondary.save();

  return { success: true };
}
